import { MediaTable, MediaTableRef } from "../MediaTable/MediaTable";
import { SearchBar } from "@kaltura/mediaspace-shared-search";
import { FilterValues, SearchAndSelectEntryList } from "@kaltura/mediaspace-shared-types";
import { useMemo, useRef, useState } from "react";
import { translate, usePostKmsData } from "@kaltura/mediaspace-shared-utils";
import { Stack } from "@kaltura/mediaspace-shared-styled";
import { SearchAndSelectTabProps } from "./SearchAndSelectTabProps";
import { useManagePlaylistData } from "./managePlaylistData";

/**
 * Search and Select component - playlist tab. allows to search entries, displays them as a table with a set of actions
 */
export const PlaylistTab = ({
    categoryId,
    mediaContext,
    filters,
    filterMenuProps,
    dataUrl,
    pageSize,
    columns,
    actions,
    conditionalMessages,
    columnsWidth,
}: SearchAndSelectTabProps) => {
    const mediaTableRef = useRef<MediaTableRef | null>(null);

    const [page, setPage] = useState(1);

    // filter values as the user set them (+ defaults)
    const [filterValues, setFilterValues] = useState<Record<string, any>>({});

    // load data only once, it will return all playlists
    const initialSearchData = useMemo(() => ({ page: 1 }), []);
    const [loadingState, response] = usePostKmsData<SearchAndSelectEntryList>(dataUrl, initialSearchData);

    const searchData = useMemo(() => ({ ...filterValues, page: 1 }), [filterValues]);

    const filteredMediaList = useManagePlaylistData(searchData, pageSize, response);

    const handleFilterChange = (values: Partial<FilterValues>) => {
        setFilterValues((filterValues) => ({ ...filterValues, ...values }));
        setPage(1);
    };
    const handleKeywordSearchSubmit = (value: string) => handleFilterChange({ keyword: value });

    const isKeywordSearch = !!filterValues["keyword"];
    const sortBy = filterValues["sortBy"] ?? "relevance";
    const handleSortByChange = (sortBy: string) => handleFilterChange({ sortBy });

    return (
        <Stack direction={"column"} gap={3}>
            <SearchBar
                keywordSearchProps={{
                    onSubmit: handleKeywordSearchSubmit,
                    value: filterValues["keyword"] as string,
                    placeholder: translate("Search"),
                    ariaLabel: translate("Search"),
                }}
            />

            <MediaTable
                ref={mediaTableRef}
                entries={filteredMediaList?.objects}
                totalCount={filteredMediaList?.totalCount ?? 0}
                isLoading={loadingState === "loading"}
                isError={loadingState === "error"}
                pageSize={pageSize}
                page={page}
                onPageChange={setPage}
                columns={columns}
                columnsWidth={columnsWidth}
                actions={(entry) => (actions ? actions(entry, false, mediaContext) : undefined)}
                stickyActionsWidth={136}
                showExploreButton={false}
                sortBy={sortBy}
                defaultSortBy={"relevance"}
                onSortByChange={handleSortByChange}
                isKeywordSearch={isKeywordSearch}
                conditionalMessages={conditionalMessages}
            />
        </Stack>
    );
};
